<template>
  <Shaft3dModelToolbarLayout
    :modifiers="[
      (hide3dTools && 'relative'),
      (disabled && 'disabled')
    ]"
  >
    <template #left>
      <p v-if="!hide3dTools">
        {{ $t('shafts.element_view') }}
      </p>
      <div
        v-if="!hide3dTools"
        class="group"
      >
        <div
          :class="{
            'group__icon group__hover group__icon--border': true,
            'group__icon--active-border': isExpanded,
          }"
          @click="isExpanded = true; select('expand')"
        >
          <ShaftExpandSVG />
        </div>
        <div
          :class="{
            'group__icon group__hover group__icon--border': true,
            'group__icon--active-border': !isExpanded,
          }"
          @click="isExpanded = false; select('collapse')"
        >
          <ShaftCollapseSVG />
        </div>
      </div>
      <div :class="{ group__border: !hide3dTools }">
        <div
          class="group group__hover"
          @click="select('reset')"
        >
          <div :class="{ 'group__icon group__icon--flip-vertically': true }">
            <ResetSVG />
          </div>
          <p>{{ $t('shafts.reset') }}</p>
        </div>
      </div>

      <Checkbox
        v-if="showSidewallCheckbox"
        id="sidewalls"
        v-model="sidewallsChecked"
        :aria-checked="sidewallsChecked"
        :aria-label="$t('general.show_sidewalls')"
        class="sidewalls-checkbox typo-small group group__border"
        :label="$t('general.show_sidewalls')"
        role="checkbox"
        type="checkbox"
        @input="$emit('displaySidewalls', !sidewallsChecked)"
      />
    </template>
    <template #right>
      <div
        v-if="undoRedoActionsEnabled"
        class="group"
      >
        <div
          :aria-label="$t('general.undo')"
          :class="{ 'group__icon group__hover': true }"
          @click="select('undo')"
        >
          <UndoArrow32SVG />
        </div>
        <div
          :aria-label="$t('general.redo')"
          :class="{ 'group__icon group__hover': true }"
          @click="select('redo')"
        >
          <RedoArrow32SVG />
        </div>
      </div>
    </template>
  </Shaft3dModelToolbarLayout>
</template>

<script setup>
import { ref, watch } from 'vue';
import Checkbox from '@/components/form/checkbox.vue';
import RedoArrow32SVG from '@/images/redo-arrow-32.svg';
import ResetSVG from '@/images/reset.svg';
import ShaftCollapseSVG from '@/images/shaft-collapse.svg';
import ShaftExpandSVG from '@/images/shaft-expand.svg';
import Shaft3dModelToolbarLayout from '@/components/shaftsV2/shaft3dModel/shaft_3d_model_toolbar_layout.vue';
import UndoArrow32SVG from '@/images/undo-arrow-32.svg';

const props = defineProps({
  /** Disables to toolbar */
  disabled: { type: Boolean, required: false, default: false },
  /** Show or hide the expand/collapse tools */
  hide3dTools: { type: Boolean, default: false },
  /** The modelValue for expand/collapse */
  modelValue: { type: String, default: 'expand' },
  /** Show or hide the sidewall checkbox */
  showSidewallCheckbox: { type: Boolean, default: false },
  /** The checked value for show sidewalls checkbox */
  sidewallsChecked: { type: Boolean, default: false },
  /** Show or hide the undo/redo actions */
  undoRedoActionsEnabled: { type: Boolean, required: false, default: false },
});

const isExpanded = ref(true);

watch(props, (value) => {
  if (value.modelValue === 'expand') isExpanded.value = true;
  if (value.modelValue === 'collapse') isExpanded.value = false;
});

const emit = defineEmits(['update:modelValue', 'displaySidewalls']);

function select(iconValue) {
  emit('update:modelValue', iconValue);
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';
.group {
  display: flex;
  align-items: center;
  height: bu(4);

  &__icon {
    &:nth-child(1) {
      margin: 0 bu(1) 0 bu(1.5);
    }
    &:last-child {
      margin-right: bu(1.5);
    }

    &--border {
      display: flex;
      justify-content: space-around;
      background-color: map-get($background-color, default);
      border: bu(0.3) solid map-get($background-color, lightest);
      border-radius: bu(1);
      padding: bu(0.3);
      width: bu(4);
    }

    &--active-border {
      background-color: map-get($background-color, darkest);
      border: bu(0.3) solid map-get($foreground-color, light);
    }

    &--active {
      background-color: map-get($background-color, darkest);
    }

    &--flip-vertically {
      -webkit-transform: scaleX(-1);
      transform: scaleX(-1);
      margin: 0 bu(1);
    }
  }

  &__hover {
    cursor: pointer;

    &:hover {
      opacity: 0.3;
    }
  }

  &__border {
    border-left: bu(0.2) solid map-get($foreground-color, darker);
  }
}

.sidewalls-checkbox {
  margin-left: bu(1);
  padding-left: bu(1);
}
</style>
